"use client";

import { useState } from "react";
import { FiMic } from "react-icons/fi";
import { Button } from "@/components/ui";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BROWSER_LABELS, type BrowserKind, type MicErrorKind, SYSTEM_STEPS, UNBLOCK_STEPS, detectBrowser } from "@/lib/microphone-error";
import { toast } from "@/lib/use-toast";
import { useScribeStore } from "@/stores/scribe-store";
const COPY: Record<MicErrorKind, {
  title: string;
  description: string;
}> = {
  blocked: {
    title: "Microphone is blocked",
    description: "Your browser isn't letting Decoda use the microphone, so the AI note can't record. Allow it once and recording will work from then on."
  },
  not_found: {
    title: "No microphone found",
    description: "We couldn't find a microphone. Plug one in or check that your headset is connected, then try again."
  },
  in_use: {
    title: "Microphone is busy",
    description: "Another app or browser tab is using the microphone. Close Zoom, Teams, FaceTime or any other tab that is recording, then try again."
  }
};
function currentBrowser(): BrowserKind {
  return typeof navigator === "undefined" ? "other" : detectBrowser(navigator.userAgent, navigator.maxTouchPoints);
}

/**
 * Opens whenever the scribe could not get the microphone, with steps for the
 * browser in front of the user. Mounted once in the console layout, next to
 * the recording PiP, because recording starts from many screens.
 */
export default function MicAccessDialog() {
  const micErrorKind = useScribeStore(s => s.micErrorKind);
  const setMicErrorKind = useScribeStore(s_0 => s_0.setMicErrorKind);
  const checkMicAccess = useScribeStore(s_1 => s_1.checkMicAccess);
  const [isChecking, setIsChecking] = useState(false);
  if (!micErrorKind) return null;
  const browser = currentBrowser();
  const {
    title,
    description
  } = COPY[micErrorKind];
  const close = () => setMicErrorKind(null);

  // Opens the same mic recording will use, without starting a recording, so
  // a fix made in settings is confirmed here before the user records again.
  // On failure the store has already set the new kind, keeping this open.
  const handleTryAgain = async () => {
    setIsChecking(true);
    try {
      await checkMicAccess();
      toast({
        title: "Microphone is ready",
        description: "Press record to start your note.",
        variant: "success"
      });
    } catch {
      // checkMicAccess reported it.
    } finally {
      setIsChecking(false);
    }
  };
  return <Dialog open onOpenChange={open => open ? null : close()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <FiMic className="text-status-error-text" size={18} />
            <DialogTitle>{title}</DialogTitle>
          </div>
          <DialogDescription className="text-body">
            {description}
          </DialogDescription>
        </DialogHeader>
        {micErrorKind === "blocked" && <DialogBody>
            <p className="mb-2 text-body text-foreground">
              To allow the microphone in {BROWSER_LABELS[browser]}:
            </p>
            <ol className="list-decimal space-y-1.5 pl-5 text-body text-foreground-secondary">
              {UNBLOCK_STEPS[browser].map(step => <li key={step}>{step}</li>)}
            </ol>
            {SYSTEM_STEPS[browser] && <p className="mt-3 text-caption text-foreground-secondary">
                {SYSTEM_STEPS[browser]}
              </p>}
          </DialogBody>}
        <DialogFooter>
          <Button variant="secondary" onClick={close}>
            Close
          </Button>
          <Button variant="primary" onClick={handleTryAgain} isLoading={isChecking} loadingText="Checking...">
            Try again
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}